import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaArrowDown, FaArrowUp, FaSearch, FaWrench, FaPlus, FaTimes, FaHelicopter, FaTruck, FaUser, FaTint } from 'react-icons/fa';
import { useApiCall } from '../hooks/useApiCall';
import { useToast } from '../components/Toast';
import TxnDetailModal from '../components/TxnDetailModal';
import SortTh from '../components/SortTh';
import '../styles/Transactions.css';

function fmtTs(ts) {
  if (!ts) return '-';
  const d = new Date(ts * 1000);
  return d.toLocaleDateString() + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const EMPTY_FORM = { type: 'OUT', tanker_id: '', aircraft_id: '', vendor_id: '', fueler_id: '', amount: '', amount_type: 'L', notes: '' };
const SPILLAGE_REASON_OPTIONS = ['Fuel cap left open', 'Nozzle leak', 'Hose rupture', 'Overfill', 'Other'];

const GAL_TO_L = 3.785411784;
const L_TO_GAL = 0.264172052;

function normaliseUnit(u) {
  if (!u) return 'L';
  return u.toString().toUpperCase().startsWith('G') ? 'GAL' : 'L';
}

function convertAmount(amount, fromUnit, toUnit) {
  const v = parseFloat(amount);
  if (!v || fromUnit === toUnit) return null;
  if (fromUnit === 'GAL' && toUnit === 'L')  return (v * GAL_TO_L).toFixed(2);
  if (fromUnit === 'L'   && toUnit === 'GAL') return (v * L_TO_GAL).toFixed(2);
  return null;
}

export default function Transactions() {
  const apiCall = useApiCall();
  const { showToast } = useToast();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [loading, setLoading] = useState(true);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [sortBy,  setSortBy]  = useState('timestamp');
  const [sortDir, setSortDir] = useState('desc');
  // Add transaction modal state
  const [showAdd, setShowAdd] = useState(false);
  const [formData, setFormData] = useState(null);
  const [addForm, setAddForm] = useState(EMPTY_FORM);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');
  const formDataLoading = useRef(false);

  const load = useCallback((p, q, t, sb, sd) => {
    setLoading(true);
    apiCall('admin/transactions', 'POST', { page: p, query: q, type: t, sort_by: sb, sort_dir: sd }).then(res => {
      if (res?.success) {
        setTransactions(res.data.transactions || []);
        setTotal(res.data.total || 0);
      }
      setLoading(false);
    });
  }, []);

  useEffect(() => { load(1, '', 'ALL', 'timestamp', 'desc'); }, []);

  const applyFilters = (q, t, p = 1, sb = sortBy, sd = sortDir) => {
    setPage(p);
    load(p, q, t, sb, sd);
  };

  const handleSearch = (e) => {
    const q = e.target.value;
    setSearch(q);
    applyFilters(q, typeFilter, 1);
  };

  const handleType = (t) => {
    setTypeFilter(t);
    applyFilters(search, t, 1);
  };

  const handleSort = (col) => {
    const dir = sortBy === col && sortDir === 'desc' ? 'asc' : 'desc';
    setSortBy(col);
    setSortDir(dir);
    applyFilters(search, typeFilter, 1, col, dir);
  };

  const handlePage = (p) => {
    setPage(p);
    load(p, search, typeFilter, sortBy, sortDir);
  };

  const totalPages = Math.ceil(total / 20);

  // ── Add transaction helpers ──────────────────────────────────────────────
  const openAdd = async () => {
    setAddForm(EMPTY_FORM);
    setAddError('');
    setShowAdd(true);
    if (!formData && !formDataLoading.current) {
      formDataLoading.current = true;
      const res = await apiCall('admin/transactions/form-data', 'POST', {});
      if (res?.success) setFormData(res.data);
      formDataLoading.current = false;
    }
  };

  const closeAdd = () => { setShowAdd(false); setAddError(''); };

  const setField = (k, v) => setAddForm(f => ({ ...f, [k]: v }));

  // When a tanker is selected, default the unit to the tanker's own unit
  const handleTankerChange = (tanker_id) => {
    const t = formData?.tankers?.find(t => String(t.id) === String(tanker_id));
    const unit = t ? normaliseUnit(t.amount_type) : 'L';
    setAddForm(f => ({ ...f, tanker_id, amount_type: unit === 'GAL' ? 'Gal.' : 'L' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAddError('');
    setAddLoading(true);
    const res = await apiCall('admin/transactions/create', 'POST', { form: addForm });
    setAddLoading(false);
    if (res?.success) {
      showToast('Transaction created successfully.', 'success');
      closeAdd();
      load(1, search, typeFilter);
      setPage(1);
    } else {
      setAddError(res?.response || 'Failed to create transaction.');
    }
  };

  const selectedTanker = formData?.tankers?.find(t => String(t.id) === String(addForm.tanker_id));
  const tankUnit = selectedTanker ? normaliseUnit(selectedTanker.amount_type) : 'L';
  const enteredUnit = normaliseUnit(addForm.amount_type);
  const conversionHint = convertAmount(addForm.amount, enteredUnit, tankUnit);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Transactions</h1>
          <p className="page-subtitle">Full history of FuelIN and FuelOUT operations</p>
        </div>
        <button className="btn btn-primary" onClick={openAdd}>
          <FaPlus style={{ marginRight: 6 }} /> Add Transaction
        </button>
      </div>

      <div className="filters-row">
        <div className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by aircraft, vendor, fueler..."
            value={search}
            onChange={handleSearch}
          />
        </div>
        <div className="filter-btns">
          {['ALL', 'OUT', 'IN', 'CORRECTION', 'SPILLAGE'].map(f => (
            <button key={f} className={`btn btn-sm ${typeFilter === f ? 'btn-primary' : 'btn-secondary'}`} onClick={() => handleType(f)}>
              {f === 'CORRECTION' ? <><FaWrench /> Corrections</> : f === 'SPILLAGE' ? <><FaTint /> Spillage</> : f}
            </button>
          ))}
        </div>
      </div>

      <div className="users-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <SortTh col="id"        label="#"             sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="type"      label="Type"          sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <th>Details</th>
              <SortTh col="tanker"    label="Tanker"        sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="gas_type"  label="Fuel Type"     sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="amount"    label="Amount"        sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="timestamp" label="Date / Time"   sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
              <SortTh col="status"    label="Status"        sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
          {loading ? (
              <tr><td colSpan={8} className="table-empty">Loading...</td></tr>
            ) : transactions.length === 0 ? (
              <tr><td colSpan={8} className="table-empty">No transactions found.</td></tr>
            ) : transactions.map(tx => {
              const isOut      = tx.type === 'OUT';
              const isIn       = tx.type === 'IN';
              const isCorr     = tx.type === 'CORRECTION';
              const isSpillage = tx.type === 'SPILLAGE';
              return (
              <tr key={tx.id} onClick={() => setSelectedTxn(tx)} className="txn-row">
                <td className="txn-id-cell">#{tx.id}</td>
                <td>
                  {isCorr
                    ? <span className="txn-badge txn-badge-corr"><FaWrench /> CORR</span>
                    : isOut
                      ? <span className="txn-badge txn-badge-out"><FaArrowUp /> OUT</span>
                      : isIn
                        ? <span className="txn-badge txn-badge-in"><FaArrowDown /> IN</span>
                        : isSpillage
                          ? <span className="txn-badge txn-badge-spillage"><FaTint /> SPILL</span>
                          : <span className="txn-badge">{tx.type}</span>
                  }
                </td>
                <td className="txn-detail-cell">
                  <div className="txn-detail-main">
                    {isCorr ? (() => {
                      const parts = (tx.notes || '').split(' | ');
                      const baseNote = parts[0] || 'Manual admin correction';
                      const corrDetail = parts[1] || null;
                      return (
                        <>
                          <span className="txn-corr-note">{baseNote}</span>
                          {corrDetail && <span className="txn-corr-detail">{corrDetail}</span>}
                        </>
                      );
                    })() : isOut ? (
                      <span className="txn-aircraft"><FaHelicopter className="txn-cell-icon" />{tx.aircraft || '—'}</span>
                    ) : isSpillage ? (
                      <span className="txn-corr-note">{tx.notes || 'Fuel spillage'}</span>
                    ) : (
                      <span className="txn-vendor">{tx.vendor || '—'}</span>
                    )}
                  </div>
                  {tx.fueler && <div className="txn-detail-sub"><FaUser className="txn-cell-icon muted" />{tx.fueler}</div>}
                </td>
                <td className="txn-meta-cell"><FaTruck className="txn-cell-icon muted" />{tx.tanker || '—'}</td>
                <td><span className="txn-fuel-chip">{tx.gas_type || '—'}</span></td>
                <td className={`txn-amount-cell ${isOut || isSpillage ? 'txn-amount-out' : isIn ? 'txn-amount-in' : isCorr ? (parseFloat(tx.amount) >= 0 ? 'txn-amount-in' : 'txn-amount-out') : ''}`}>
                  {isOut || isSpillage ? '−' : isIn ? '+' : isCorr ? (parseFloat(tx.amount) >= 0 ? '+' : '−') : ''}{Math.abs(tx.amount)} <span className="txn-unit">{tx.amount_type}</span>
                </td>
                <td className="txn-date-cell">{fmtTs(tx.timestamp)}</td>
                <td><span className={`txn-status ${tx.status || 'completed'}`}>{tx.status || 'completed'}</span></td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination-row">
          <button className="btn btn-sm btn-secondary" disabled={page <= 1} onClick={() => handlePage(page - 1)}>Prev</button>
          <span>Page {page} of {totalPages} &nbsp;({total} total)</span>
          <button className="btn btn-sm btn-secondary" disabled={page >= totalPages} onClick={() => handlePage(page + 1)}>Next</button>
        </div>
      )}

      {/* ── Add Transaction Modal ─────────────────────────────────────── */}
      {showAdd && (
        <div className="modal-overlay" onClick={e => e.stopPropagation()}>
          <div className="modal-content modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Manual Transaction</h3>
              <button className="modal-close-btn" onClick={closeAdd}><FaTimes /></button>
            </div>
            <div className="modal-body">
              {!formData ? (
                <div style={{ textAlign: 'center', padding: '40px 0', color: '#8896a5' }}>Loading form data…</div>
              ) : (
                <form id="add-txn-form" onSubmit={handleSubmit}>
                  {/* Type */}
                  <div className="form-group">
                    <label className="form-label">Transaction Type</label>
                    <div className="txn-type-toggle">
                      <button type="button" className={`txn-toggle-btn ${addForm.type === 'OUT' ? 'active-out' : ''}`} onClick={() => { setField('type', 'OUT'); setField('vendor_id', ''); }}>
                        <FaArrowUp /> Fuel Out
                      </button>
                      <button type="button" className={`txn-toggle-btn ${addForm.type === 'IN' ? 'active-in' : ''}`} onClick={() => { setField('type', 'IN'); setField('aircraft_id', ''); }}>
                        <FaArrowDown /> Fuel In
                      </button>
                      <button type="button" className={`txn-toggle-btn ${addForm.type === 'SPILLAGE' ? 'active-spillage' : ''}`} onClick={() => { setField('type', 'SPILLAGE'); setField('aircraft_id', ''); setField('vendor_id', ''); }}>
                        <FaTint /> Spillage
                      </button>
                    </div>
                  </div>

                  {/* Tanker */}
                  <div className="form-group">
                    <label className="form-label">Tanker <span className="required">*</span></label>
                    <select className="form-input" value={addForm.tanker_id} onChange={e => handleTankerChange(e.target.value)} required>
                      <option value="">- Select tanker -</option>
                      {formData.tankers.map(t => (
                        <option key={t.id} value={t.id}>{t.name} ({t.gas_type}) - {t.amount} / {t.amount_cap} {t.amount_type}</option>
                      ))}
                    </select>
                    {selectedTanker && (
                      <div className="txn-tanker-bar">
                        <div className="txn-tanker-fill" style={{ width: `${selectedTanker.amount_cap > 0 ? Math.round((selectedTanker.amount / selectedTanker.amount_cap) * 100) : 0}%` }} />
                        <span className="txn-tanker-pct">{selectedTanker.amount_cap > 0 ? Math.round((selectedTanker.amount / selectedTanker.amount_cap) * 100) : 0}% full</span>
                      </div>
                    )}
                  </div>

                  {/* Aircraft (OUT only) */}
                  {addForm.type === 'OUT' && (
                    <div className="form-group">
                      <label className="form-label">Aircraft <span className="required">*</span></label>
                      <select className="form-input" value={addForm.aircraft_id} onChange={e => setField('aircraft_id', e.target.value)} required>
                        <option value="">- Select aircraft -</option>
                        {formData.aircrafts.map(a => (
                          <option key={a.id} value={a.id}>{a.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Vendor (IN only) */}
                  {addForm.type === 'IN' && (
                    <div className="form-group">
                      <label className="form-label">Vendor <span className="required">*</span></label>
                      <select className="form-input" value={addForm.vendor_id} onChange={e => setField('vendor_id', e.target.value)} required>
                        <option value="">- Select vendor -</option>
                        {formData.vendors.map(v => (
                          <option key={v.id} value={v.id}>{v.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Spillage reason (SPILLAGE only) */}
                  {addForm.type === 'SPILLAGE' && (
                    <div className="form-group">
                      <label className="form-label">Spillage Reason <span className="required">*</span></label>
                      <select className="form-input" value={addForm.notes} onChange={e => setField('notes', e.target.value)} required>
                        <option value="">- Select reason -</option>
                        {SPILLAGE_REASON_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                      </select>
                    </div>
                  )}

                  {/* Fueler */}
                  <div className="form-group">
                    <label className="form-label">Responsible Fueler</label>
                    <select className="form-input" value={addForm.fueler_id} onChange={e => setField('fueler_id', e.target.value)}>
                      <option value="">- Admin (myself) -</option>
                      {formData.fuelers.map(f => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Amount */}
                  <div className="form-group">
                    <label className="form-label">Amount <span className="required">*</span></label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        type="number"
                        className="form-input"
                        placeholder="0.00"
                        min="0.01"
                        step="0.01"
                        value={addForm.amount}
                        onChange={e => setField('amount', e.target.value)}
                        required
                        style={{ flex: 1 }}
                      />
                      <select className="form-input" style={{ width: 90 }} value={addForm.amount_type} onChange={e => setField('amount_type', e.target.value)}>
                        <option value="L">L</option>
                        <option value="Gal.">Gal.</option>
                      </select>
                    </div>
                    {selectedTanker && addForm.amount > 0 && conversionHint && (
                      <div className="txn-unit-hint">
                        = <strong>{conversionHint} {selectedTanker.amount_type}</strong> (tank stores in {selectedTanker.amount_type})
                      </div>
                    )}
                    {selectedTanker && addForm.amount > 0 && !conversionHint && (
                      <div className="txn-unit-hint txn-unit-hint-match">
                        Same unit as tank - no conversion needed
                      </div>
                    )}
                  </div>

                  {/* Notes (not for SPILLAGE, which uses the reason dropdown) */}
                  {addForm.type !== 'SPILLAGE' && (
                    <div className="form-group">
                      <label className="form-label">Notes <span style={{ color: '#8896a5', fontWeight: 400 }}>(optional)</span></label>
                      <textarea
                        className="form-input"
                        rows={2}
                        placeholder="e.g. Scheduled maintenance fill, manual entry..."
                        value={addForm.notes}
                        onChange={e => setField('notes', e.target.value)}
                        style={{ resize: 'vertical' }}
                      />
                    </div>
                  )}

                  {addError && <div className="form-error">{addError}</div>}
                </form>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={closeAdd}>Cancel</button>
              <button
                type="submit"
                form="add-txn-form"
                className="btn btn-primary"
                disabled={addLoading}
              >
                {addLoading ? 'Creating…' : 'Create Transaction'}
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedTxn && <TxnDetailModal tx={selectedTxn} onClose={() => setSelectedTxn(null)} />}
    </div>
  );
}

